import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AvatarPersonIcon } from "./icons";
import { competitionCopy, organizerHandle } from "./leagueFormat";

// CREATED BY — one component, every screen.
//
// Client Rev: "CREATED BY is a single League Play component; make sure this block
// matches on all pages where it exists. Treat the organizer avatar + CREATED BY +
// username block as one reusable component so Public League landing, Private
// League landing, and League Pages do not drift visually from one another."
//
// They had drifted into three:
//
//   League Pages (LeagueShell)      36px avatar · CREATED BY eyebrow · bold name
//   Private landing (invite card)   32px avatar · CREATED BY eyebrow · semibold
//   Public landing                  20px avatar · NO EYEBROW AT ALL · grey name
//
// The public one is the block the client screenshotted — just a tiny avatar and a
// grey handle, with the eyebrow missing entirely.
//
// THE APPROVED TREATMENT, which this component now is, is the League Pages one —
// the fullest of the three and the one on the most surfaces (League Home for both
// league types, plus League Details):
//
//   avatar    36px (h-9 w-9), rounded-full, object-cover, #F3F4F6 fallback disc
//   eyebrow   10px / font-semibold / uppercase / tracking-wider / text-secondary
//   username  text-sm / font-bold / #0F172A
//   spacing   10px between avatar and text (gap-2.5), 0 between the two lines
//   alignment items-center; the text column truncates rather than wrapping
//
// So the private landing's name goes semibold -> bold and its avatar 32 -> 36px,
// and the public landing gains the eyebrow and the same 36px avatar. That is the
// drift the client asked to remove; it cannot come back, because there is now one
// definition rather than three.
//
// `align` is the ONLY thing a caller may vary, because the client asks for
// different alignment on different screens ("CREATED BY block stays left aligned
// beneath the metadata" on mobile, centred under the desktop stat strip). It moves
// the whole block; it never changes its internals.
// `avatarHref` is the ONE other thing a caller may vary, and it is opt-in.
//
// Client: "Inside the competition header - Make the profile avatar on the public
// and private league home next to CREATED BY go to the organizers' league
// dashboard page so members can DM them easily."
//
// INSIDE the competition only, which is why the host passes it rather than this
// component working it out. The same block renders on the public and private
// LANDING pages, where the reader may not be a member — sending them to a league
// profile from a page whose whole job is to decide whether to join would be a
// dead end. LeagueShell is the only caller that turns it on.
//
// Only the AVATAR becomes a link, not the handle, exactly as asked. The eyebrow
// and the username keep their treatment, so the block still matches the other
// screens the client asked it not to drift from.
const CreatedByBlock = ({ game, align = "left", className = "", avatarHref = null }) => {
  const [broken, setBroken] = useState(false);
  const src = game?.user?.image;
  const showImage = !!src && !broken;

  return (
    <span
      className={`flex min-w-0 items-center gap-2.5 ${
        align === "center" ? "justify-center" : ""
      } ${className}`}
    >
      {(() => {
        const avatar = showImage ? (
          <img
            src={src}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
            onError={() => setBroken(true)}
          />
        ) : (
          <AvatarPersonIcon />
        );
        const shell = "flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#F3F4F6]";
        return avatarHref ? (
          <Link
            to={avatarHref}
            aria-label={`Open ${organizerHandle(game)}'s league profile`}
            title={`Open ${organizerHandle(game)}'s league profile`}
            className={`${shell} transition-opacity hover:opacity-80`}
          >
            {avatar}
          </Link>
        ) : (
          <span className={shell}>{avatar}</span>
        );
      })()}
      <span className="min-w-0 text-left">
        {/* Client 0014889: "Created by -> Hosted by". QUSHi runs a cash
            game, so "created" understates it and "hosted" is the word they
            used on the Starting soon card too. */}
        <span className="block text-[10px] font-semibold uppercase tracking-wider text-secondary">
          {competitionCopy(game).createdBy}
        </span>
        {/* The handle, never a real name — the client's standing rule for QUSHi.
            organizerHandle is what enforces that, in one place. */}
        <span className="block truncate text-sm font-bold text-[#0F172A]">
          {organizerHandle(game)}
        </span>
      </span>
    </span>
  );
};

export default CreatedByBlock;
